<template>
  <app-content :title="'参考事实 / 凭证生成参考素材池'">
    <div class="reference-fact-page">
      <div class="rf-notice">
        <i class="h-icon-info"></i>
        <span>参考事实是凭证生成参考素材池，用于沉淀可复用的业务事实，增强银行流水入账建议与解释。系统不会直接自动生成凭证，导入后仍需会计在预览页面审核确认。</span>
      </div>

      <div class="rf-tabs">
        <button class="rf-tab-btn" :class="{ active: activeTab === 'query' }" @click="activeTab = 'query'">查询参考事实</button>
        <button class="rf-tab-btn" :class="{ active: activeTab === 'import' }" @click="activeTab = 'import'">导入参考事实</button>
        <button class="rf-tab-btn" :class="{ active: activeTab === 'batch' }" @click="activeTab = 'batch'">按批次查询</button>
      </div>

      <!-- ========== 导入 Tab ========== -->
      <div v-if="activeTab === 'import'" class="rf-panel">
        <div class="rf-form">
          <!-- ===== 灵活导入模式 ===== -->
          <div v-if="importForm.importMode === 'flexible'" class="rf-flexible-import">
            <div class="rf-form-row">
              <label><span class="required">*</span> 事实类型：</label>
              <select v-model="flexibleFactType" class="form-input">
                <option value="PURCHASE_PAYABLE_SUMMARY">采购应付汇总</option>
                <option value="LOGISTICS_PAYABLE_SUMMARY">物流应付明细汇总</option>
              </select>
            </div>
            <div v-if="flexibleStep === 'upload'">
              <div class="rf-form-row">
                <label><span class="required">*</span> 选择 Excel：</label>
                <input type="file" ref="flexibleFileInput" @change="handleFlexibleFileChange" accept=".xlsx,.xls" class="form-input" />
              </div>
              <div v-if="flexibleFileName" class="rf-form-row" style="margin-top:8px">
                <label></label>
                <span class="rf-excel-name">{{ flexibleFileName }}</span>
                <button class="btn btn-primary btn-sm" style="margin-left:12px" @click="loadFlexibleData" :disabled="flexibleFileLoading">{{ flexibleFileLoading ? '读取中...' : '读取并解析' }}</button>
              </div>
            </div>

            <!-- 字段映射 -->
            <div v-if="flexibleStep === 'mapping' || flexibleStep === 'preview'" class="rf-flexible-mapping">
              <div class="rf-subtitle">字段映射</div>
              <p style="font-size:12px;color:#909399;margin:4px 0 12px">Excel 表头已自动匹配。可手动调整下拉选择。</p>
              <table class="rf-table rf-mapping-table">
                <colgroup><col style="width:130px"><col style="width:40px"><col style="width:auto"></colgroup>
                <thead><tr><th>标准字段</th><th>必填</th><th>Excel 实际列</th></tr></thead>
                <tbody>
                  <tr v-for="(item, mi) in flexibleMapping" :key="mi" :class="{ 'rf-mapping-matched': item.excelHeader, 'rf-mapping-missing': !item.excelHeader && isRequiredField(item.standardKey) }">
                    <td>{{ item.standardKey }}</td>
                    <td><span v-if="isRequiredField(item.standardKey)" class="required">*</span></td>
                    <td>
                      <select v-model="item.excelHeader" class="form-input rf-mapping-select" :disabled="flexibleStep === 'preview'">
                        <option value="">--未匹配--</option>
                        <option v-for="h in flexibleHeaders" :key="h" :value="h">{{ h }}</option>
                      </select>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="flexibleUnmappedHeaders.length" class="rf-flexible-unmapped">
                <i class="h-icon-info"></i> 以下 Excel 列未被映射（不影响导入）：{{ flexibleUnmappedHeaders.join('、') }}
              </div>
            </div>

            <!-- 预览 -->
            <div v-if="flexibleStep === 'preview'" class="rf-flexible-preview">
              <div class="rf-subtitle">预览（前 5 行 — 共 {{ flexibleTotalRows }} 行）</div>
              <div class="rf-table-wrapper">
                <table class="rf-table">
                  <thead><tr><th v-for="item in visibleMappingColumns()" :key="item.standardKey" class="rf-nowrap">{{ item.standardKey }}</th></tr></thead>
                  <tbody>
                    <tr v-for="(row, ri) in flexiblePreviewRows" :key="ri">
                      <td v-for="item in visibleMappingColumns()" :key="item.standardKey">{{ row[item.standardKey] || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="flexibleValidationErrors.length" class="rf-flexible-errors">
                <div class="rf-subtitle" style="color:#f56c6c">校验问题</div>
                <div v-for="(err, ei) in flexibleValidationErrors" :key="ei" class="rf-error-item">{{ err }}</div>
              </div>
            </div>

            <div class="rf-form-actions" style="margin-top:14px">
              <template v-if="flexibleStep === 'upload'">
                <button class="btn btn-default" @click="switchImportMode('excel')">返回标准导入</button>
              </template>
              <template v-if="flexibleStep === 'mapping'">
                <button class="btn btn-default" @click="resetFlexibleImport">重新选择文件</button>
                <button class="btn btn-primary" @click="previewFlexibleMapping">预览</button>
              </template>
              <template v-if="flexibleStep === 'preview'">
                <button class="btn btn-default" @click="backToFlexibleMapping">返回修改映射</button>
                <button class="btn btn-primary" @click="submitFlexibleImport" :disabled="flexibleSubmitting">{{ flexibleSubmitting ? '导入中...' : '确认导入' }}</button>
              </template>
            </div>
          </div>

          <!-- ===== 标准导入模式 ===== -->
          <template v-else>
            <div class="rf-form-row">
              <label><span class="required">*</span> 参考事实类型：</label>
              <select v-model="importForm.factType" class="form-input">
                <option value="PURCHASE_PAYABLE_SUMMARY">采购应付汇总</option>
                <option value="LOGISTICS_PAYABLE_SUMMARY">物流应付明细汇总</option>
              </select>
            </div>
            <div class="rf-form-row">
              <label><span class="required">*</span> 期间：</label>
              <input v-model="importForm.period" type="text" class="form-input" placeholder="2026-05" />
            </div>
            <div class="rf-form-row">
              <label>导入批次ID：</label>
              <input v-model="importForm.importBatchId" type="text" class="form-input" placeholder="留空自动生成" />
            </div>
            <div class="rf-form-row">
              <label>币种：</label>
              <input v-model="importForm.defaultCurrency" type="text" class="form-input" placeholder="默认CNY" />
            </div>
            <div class="rf-form-row">
              <label><span class="required">*</span> 导入方式：</label>
              <div class="rf-import-mode-switch">
                <button class="rf-mode-btn" :class="{ active: importForm.importMode === 'excel' }" @click="switchImportMode('excel')">上传 Excel</button>
                <button class="rf-mode-btn" :class="{ active: importForm.importMode === 'json' }" @click="switchImportMode('json')">备用：粘贴 JSON rows</button>
                <button class="rf-mode-btn" :class="{ active: importForm.importMode === 'flexible' }" @click="switchImportMode('flexible')">灵活导入</button>
              </div>
            </div>
            <div v-if="importForm.importMode === 'json'">
              <div class="rf-form-row"><label><span class="required">*</span> 数据（JSON rows）：</label></div>
              <textarea v-model="importForm.jsonText" class="rf-json-input" placeholder="粘贴JSON数组 rows，每行包含对应字段"></textarea>
            </div>
            <div v-else class="rf-excel-upload">
              <div class="rf-form-row">
                <label><span class="required">*</span> 选择 Excel 文件：</label>
                <input type="file" ref="excelFileInput" @change="handleExcelFileChange" accept=".xlsx,.xls" class="form-input" />
              </div>
              <div v-if="importForm.excelFileName" class="rf-form-row"><label></label><span class="rf-excel-name">{{ importForm.excelFileName }}</span></div>
            </div>
            <div class="rf-form-actions">
              <button class="btn btn-primary" @click="handleImport" :disabled="importing"><i class="h-icon-upload"></i>{{ importing ? '导入中...' : '执行导入' }}</button>
              <button v-if="importForm.importMode === 'json'" class="btn btn-default" @click="fillSamplePurchaseRows">填入采购应付示例</button>
              <button v-if="importForm.importMode === 'json'" class="btn btn-default" @click="fillSampleLogisticsRows">填入物流应付示例</button>
            </div>
          </template>
        </div>
        <div v-if="importResult" class="rf-result">
          <div class="rf-result-header"><i :class="importResult.failedRows > 0 ? 'h-icon-warning' : 'h-icon-success'"></i><span>导入结果</span></div>
          <div class="rf-result-grid">
            <div class="rf-result-item"><span class="rf-result-label">导入批次</span><span class="rf-result-value mono">{{ importResult.importBatchId }}</span></div>
            <div class="rf-result-item"><span class="rf-result-label">总行数</span><span class="rf-result-value">{{ importResult.totalRows }}</span></div>
            <div class="rf-result-item success"><span class="rf-result-label">成功</span><span class="rf-result-value">{{ importResult.successRows }}</span></div>
            <div class="rf-result-item" :class="{ failed: importResult.failedRows > 0 }"><span class="rf-result-label">失败</span><span class="rf-result-value">{{ importResult.failedRows }}</span></div>
          </div>
          <div v-if="importResult.warnings && importResult.warnings.length" class="rf-import-warnings"><div class="rf-subtitle">警告</div><div v-for="(w, wi) in importResult.warnings" :key="wi" class="rf-warning-item">{{ w }}</div></div>
          <div v-if="importResult.errors && importResult.errors.length" class="rf-import-errors"><div class="rf-subtitle">错误</div><div v-for="(e, ei) in importResult.errors" :key="ei" class="rf-error-item">{{ e }}</div></div>
          <div class="rf-result-actions"><button class="btn btn-default btn-sm" @click="queryBatchResult(importResult.importBatchId)">查看该批次</button></div>
        </div>
      </div>

      <!-- ========== 查询 Tab ========== -->
      <div v-if="activeTab === 'query'" class="rf-panel">
        <div class="rf-form query-form">
          <div class="rf-form-row">
            <label>事实覆盖期间（可选）</label>
            <input v-model="queryForm.period" type="text" class="form-input" placeholder="2025-01~2025-10" />
          </div>
          <div class="rf-form-row">
            <label>事实类型</label>
            <select v-model="queryForm.factType" class="form-input">
              <option value="">全部</option><option value="PURCHASE_PAYABLE_SUMMARY">采购应付汇总</option><option value="LOGISTICS_PAYABLE_SUMMARY">物流应付明细汇总</option>
            </select>
          </div>
          <div class="rf-form-row">
            <label>生命周期状态</label>
            <select v-model="queryForm.status" class="form-input">
              <option value="ACTIVE">ACTIVE - 可用于建议</option>
              <option value="BLOCKED">BLOCKED - 不参与建议</option>
              <option value="IGNORED">IGNORED - 已忽略</option>
              <option value="SUPERSEDED">SUPERSEDED - 已取代</option>
              <option value="ARCHIVED">ARCHIVED - 已归档</option>
              <option value="ALL">全部</option>
            </select>
          </div>
          <div class="rf-form-row">
            <label>往来匹配状态</label>
            <select v-model="queryForm.resolution" class="form-input">
              <option value="">全部</option>
              <option value="UNRESOLVED">未匹配</option>
              <option value="AUTO_RESOLVED">自动匹配</option>
              <option value="MANUAL_RESOLVED">手动匹配</option>
              <option value="RESOLUTION_IGNORED">已忽略匹配</option>
            </select>
          </div>
          <div class="rf-form-row">
            <label>对手角色</label>
            <select v-model="queryForm.counterpartyRole" class="form-input">
              <option value="">全部</option><option value="SUPPLIER">供应商</option><option value="LOGISTICS_PROVIDER">物流商</option>
            </select>
          </div>
          <div class="rf-form-row">
            <label>对手名称</label>
            <input v-model="queryForm.counterpartyName" type="text" class="form-input" placeholder="对手方名称" />
          </div>
          <div class="rf-form-row">
            <label>对手源ID</label>
            <input v-model="queryForm.counterpartySourceId" type="text" class="form-input" placeholder="供应商ID / 物流商ID" />
          </div>
          <div class="rf-form-row">
            <label>往来单位ID</label>
            <input v-model="queryForm.bizRelEntityId" type="text" class="form-input" placeholder="关系服务中的统一企业对象ID" />
          </div>
          <div class="rf-query-actions">
            <button class="btn btn-primary" @click="handleQuery" :disabled="querying">{{ querying ? '查询中...' : '查询' }}</button>
            <button class="btn btn-default" @click="resetQueryForm">重置</button>
          </div>
        </div>
        <div v-if="queryResult" class="rf-fact-list">
          <div class="rf-fact-list-header"><span>共 {{ queryResult.totalRows }} 条参考事实素材</span></div>
          <div v-if="!queryResult.facts || queryResult.facts.length === 0" class="rf-empty">暂无参考事实数据</div>
          <div v-else class="rf-table-wrapper">
            <table class="rf-table">
              <colgroup><col style="width:90px"><col style="width:85px"><col style="width:260px"><col style="width:70px"><col style="width:80px"><col style="width:120px"><col style="width:120px"><col style="width:140px"><col style="width:105px"><col style="width:80px"></colgroup>
              <thead><tr><th class="rf-nowrap">事实类型</th><th class="rf-nowrap">期间</th><th>对手方</th><th>往来单位</th><th>往来匹配状态</th><th class="rf-num">支付金额</th><th class="rf-num">期末余额</th><th>风险标识</th><th class="rf-nowrap">状态</th><th class="rf-nowrap rf-th-actions">操作</th></tr></thead>
              <tbody><tr v-for="(fact, fi) in queryResult.facts" :key="fi">
                <td class="rf-nowrap"><span class="rf-type-tag" :title="factTypeFull(fact.factType)">{{ factTypeShort(fact.factType) }}</span></td>
                <td class="rf-nowrap">{{ formatPeriod(fact.period) }}</td>
                <td class="rf-counterparty"><div class="rf-cp-name" :title="fact.counterpartyNameSnapshot || '-'">{{ fact.counterpartyNameSnapshot || '-' }}</div><div class="rf-cp-sub" v-if="fact.bizRelEntityId || fact.counterpartySourceId" :title="fact.bizRelEntityId || fact.counterpartySourceId">{{ fact.bizRelEntityId || fact.counterpartySourceId }}</div></td>
                <td class="rf-bizrel-cell"><span class="rf-bizrel-text" :class="matchStatusClass(fact.bizRelMatchStatus)" :title="fact.bizRelMatchStatus || '-'">{{ matchStatusCn(fact.bizRelMatchStatus) }}</span></td>
                <td class="rf-nowrap"><span class="rf-badge" :class="resolutionClass(fact.bizRelResolutionStatus)" :title="fact.bizRelResolutionStatus || '-'">{{ resolutionCn(fact.bizRelResolutionStatus) }}</span></td>
                <td class="rf-num" :class="{ 'rf-num-neg': isNegative(fact.paymentAmount) }">{{ fact.paymentAmount ? fmt(fact.paymentAmount) : '-' }}</td>
                <td class="rf-num" :class="{ 'rf-num-neg': isNegative(fact.endingBalance) }">{{ fact.endingBalance ? fmt(fact.endingBalance) : '-' }}</td>
                <td class="rf-risk-cell"><span v-if="hasRiskFlags(fact.riskFlags)" class="rf-risk-chips"><template v-for="(flag, fi2) in visibleRiskFlags(fact.riskFlags)"><span v-if="isPrimaryRisk(flag.raw)" :key="fi2" class="rf-risk-chip" :class="riskChipClass(flag.raw)" :title="flag.raw">{{ flag.cn }}</span><span v-else :key="fi2" class="rf-risk-hint" :title="flag.raw">{{ flag.cn }}</span></template><span v-if="overflowRiskCount(fact.riskFlags) > 0" class="rf-risk-chip rf-risk-overflow" :title="allRiskFlagsTitle(fact.riskFlags)">+{{ overflowRiskCount(fact.riskFlags) }}</span></span><span v-else class="rf-no-risk">-</span></td>
                <td class="rf-nowrap"><span class="rf-badge" :class="statusClass(fact.status)" :title="fact.status || '-'">{{ statusCn(fact.status) }}</span></td>
                <td class="rf-actions"><button class="rf-act-btn rf-act-edit" @click="openEditDrawer(fact)">编辑</button><span class="rf-more-wrap"><button class="rf-act-btn rf-act-more" @click.stop="toggleMoreMenu(fi)" title="治理操作">···</button><div v-if="moreMenuIndex === fi" class="rf-more-menu"><button class="rf-more-item" v-for="(act, ai) in listingActions(fact)" :key="ai" @click="openGovernanceDialog(act.key, fact); moreMenuIndex = -1">{{ act.label }}</button><div v-if="listingActions(fact).length === 0" class="rf-more-item rf-more-item-disabled">无可执行动作</div></div></span></td>
              </tr></tbody>
            </table>
          </div>
          <div class="rf-pagination">
            <select v-model="queryPager.limit" class="form-input rf-page-size" @change="changePageSize">
              <option :value="20">20条/页</option><option :value="50">50条/页</option><option :value="100">100条/页</option>
            </select>
            <span class="rf-page-info">共 {{ queryResult.totalRows }} 条，{{ queryPager.page }}/{{ queryPager.totalPages }} 页</span>
            <button class="btn btn-default btn-sm" :disabled="queryPager.page <= 1" @click="goPage(queryPager.page - 1)">上一页</button>
            <button class="btn btn-default btn-sm" :disabled="queryPager.page >= queryPager.totalPages" @click="goPage(queryPager.page + 1)">下一页</button>
          </div>
        </div>
      </div>

      <!-- ========== 批次 Tab ========== -->
      <div v-if="activeTab === 'batch'" class="rf-panel">
        <div class="rf-batch-layout">
          <div class="rf-form rf-batch-query-form">
            <div class="rf-form-row">
              <label><span class="required">*</span> 导入批次ID</label>
              <input v-model="batchForm.importBatchId" type="text" class="form-input" placeholder="arf-xxx" />
            </div>
            <div class="rf-form-row">
              <label>状态</label>
              <select v-model="batchForm.status" class="form-input">
                <option value="ACTIVE">ACTIVE - 可用于建议</option>
                <option value="BLOCKED">BLOCKED - 不参与建议</option>
                <option value="IGNORED">IGNORED - 已忽略</option>
                <option value="SUPERSEDED">SUPERSEDED - 已取代</option>
                <option value="ARCHIVED">ARCHIVED - 已归档</option>
                <option value="ALL">全部</option>
              </select>
            </div>
            <div class="rf-form-row">
              <label>往来匹配状态</label>
              <select v-model="batchForm.resolution" class="form-input">
                <option value="">全部</option>
                <option value="UNRESOLVED">未匹配</option>
                <option value="AUTO_RESOLVED">自动匹配</option>
                <option value="MANUAL_RESOLVED">手动匹配</option>
                <option value="RESOLUTION_IGNORED">已忽略匹配</option>
              </select>
            </div>
            <div class="rf-query-actions">
              <button class="btn btn-primary" @click="handleBatchQuery" :disabled="querying">{{ querying ? '查询中...' : '查询该批次' }}</button>
            </div>
          </div>
          <div class="rf-batch-sidebar">
            <div class="rf-batch-sidebar-header">
              <span>最近导入批次</span>
              <button class="btn btn-default btn-xs" @click="loadRecentBatches" :disabled="loadingRecentBatches">{{ loadingRecentBatches ? '刷新中...' : '刷新' }}</button>
            </div>
            <div v-if="!recentBatches.length" class="rf-batch-empty">暂无可用批次</div>
            <div v-else class="rf-batch-list">
              <button
                v-for="batch in recentBatches"
                :key="batch.importBatchId"
                class="rf-batch-card"
                @click="selectRecentBatch(batch)"
              >
                <div class="rf-batch-card-top">
                  <span class="rf-batch-card-id">{{ batch.importBatchId }}</span>
                  <span class="rf-batch-card-count">{{ batch.totalRows }} 条</span>
                </div>
                <div class="rf-batch-card-meta">{{ factTypeShort(batch.factType) }} · {{ formatPeriod(batch.period) }}</div>
                <div class="rf-batch-card-meta">活跃 {{ batch.activeRows || 0 }} / 已归档 {{ batch.archivedRows || 0 }}</div>
              </button>
            </div>
          </div>
        </div>
        <div v-if="queryResult && queryResult.facts" class="rf-fact-list">
          <div class="rf-fact-list-header">
            <span>批次 {{ batchForm.importBatchId }}：共 {{ queryResult.totalRows }} 条参考事实素材</span>
            <button class="btn btn-default btn-sm" style="margin-left:12px" @click="openGovernanceDialog('archiveBatch')">归档整个批次</button>
            <button class="btn btn-default btn-sm" style="margin-left:8px" @click="openGovernanceDialog('ignoreBatch')">批量忽略</button>
            <button class="btn btn-default btn-sm" style="margin-left:8px" @click="openGovernanceDialog('rematchBizrelBatch')">批量重新匹配往来单位</button>
          </div>
          <div class="rf-table-wrapper">
            <table class="rf-table">
              <colgroup><col style="width:90px"><col style="width:85px"><col style="width:260px"><col style="width:70px"><col style="width:80px"><col style="width:120px"><col style="width:120px"><col style="width:140px"><col style="width:105px"><col style="width:80px"></colgroup>
              <thead><tr><th class="rf-nowrap">事实类型</th><th class="rf-nowrap">期间</th><th>对手方</th><th>往来单位</th><th>往来匹配状态</th><th class="rf-num">支付金额</th><th class="rf-num">期末余额</th><th>风险标识</th><th class="rf-nowrap">状态</th><th class="rf-nowrap rf-th-actions">操作</th></tr></thead>
              <tbody><tr v-for="(fact, fi) in queryResult.facts" :key="fi">
                <td class="rf-nowrap"><span class="rf-type-tag" :title="factTypeFull(fact.factType)">{{ factTypeShort(fact.factType) }}</span></td>
                <td class="rf-nowrap">{{ formatPeriod(fact.period) }}</td>
                <td class="rf-counterparty"><div class="rf-cp-name" :title="fact.counterpartyNameSnapshot || '-'">{{ fact.counterpartyNameSnapshot || '-' }}</div><div class="rf-cp-sub" v-if="fact.bizRelEntityId || fact.counterpartySourceId" :title="fact.bizRelEntityId || fact.counterpartySourceId">{{ fact.bizRelEntityId || fact.counterpartySourceId }}</div></td>
                <td class="rf-bizrel-cell"><span class="rf-bizrel-text" :class="matchStatusClass(fact.bizRelMatchStatus)" :title="fact.bizRelMatchStatus || '-'">{{ matchStatusCn(fact.bizRelMatchStatus) }}</span></td>
                <td class="rf-nowrap"><span class="rf-badge" :class="resolutionClass(fact.bizRelResolutionStatus)" :title="fact.bizRelResolutionStatus || '-'">{{ resolutionCn(fact.bizRelResolutionStatus) }}</span></td>
                <td class="rf-num" :class="{ 'rf-num-neg': isNegative(fact.paymentAmount) }">{{ fact.paymentAmount ? fmt(fact.paymentAmount) : '-' }}</td>
                <td class="rf-num" :class="{ 'rf-num-neg': isNegative(fact.endingBalance) }">{{ fact.endingBalance ? fmt(fact.endingBalance) : '-' }}</td>
                <td class="rf-risk-cell"><span v-if="hasRiskFlags(fact.riskFlags)" class="rf-risk-chips"><template v-for="(flag, fi2) in visibleRiskFlags(fact.riskFlags)"><span v-if="isPrimaryRisk(flag.raw)" :key="fi2" class="rf-risk-chip" :class="riskChipClass(flag.raw)" :title="flag.raw">{{ flag.cn }}</span><span v-else :key="fi2" class="rf-risk-hint" :title="flag.raw">{{ flag.cn }}</span></template><span v-if="overflowRiskCount(fact.riskFlags) > 0" class="rf-risk-chip rf-risk-overflow" :title="allRiskFlagsTitle(fact.riskFlags)">+{{ overflowRiskCount(fact.riskFlags) }}</span></span><span v-else class="rf-no-risk">-</span></td>
                <td class="rf-nowrap"><span class="rf-badge" :class="statusClass(fact.status)" :title="fact.status || '-'">{{ statusCn(fact.status) }}</span></td>
                <td class="rf-actions"><button class="rf-act-btn rf-act-edit" @click="openEditDrawer(fact)">编辑</button><span class="rf-more-wrap"><button class="rf-act-btn rf-act-more" @click.stop="toggleMoreMenu(fi)" title="治理操作">···</button><div v-if="moreMenuIndex === fi" class="rf-more-menu"><button class="rf-more-item" v-for="(act, ai) in listingActions(fact)" :key="ai" @click="openGovernanceDialog(act.key, fact); moreMenuIndex = -1">{{ act.label }}</button><div v-if="listingActions(fact).length === 0" class="rf-more-item rf-more-item-disabled">无可执行动作</div></div></span></td>
              </tr></tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ========== 编辑抽屉 ========== -->
    <div v-if="editDrawerOpen" ref="drawerOverlay" class="rf-drawer-overlay" @click.self="closeEditDrawer">
      <div class="rf-drawer">
        <div class="rf-drawer-header">
          <h3>编辑参考事实</h3>
          <p>修改参考素材池中的事实字段后，请填写变更原因</p>
          <button class="rf-drawer-close" @click="closeEditDrawer"><i class="h-icon-close"></i></button>
        </div>
        <div class="rf-drawer-body">
          <div class="rf-drawer-summary">
            <span class="rf-summary-item">生命周期：<span class="rf-badge" :class="statusClass(editingFact.status)" :title="editingFact.status || '-'">{{ statusCn(editingFact.status) }}</span></span>
            <span class="rf-summary-item">往来单位匹配：<span class="rf-badge" :class="matchStatusClass(editingFact.bizRelMatchStatus)" :title="editingFact.bizRelMatchStatus || '-'">{{ matchStatusCn(editingFact.bizRelMatchStatus) }}</span></span>
            <span class="rf-summary-item">往来匹配状态：<span class="rf-badge" :class="resolutionClass(editingFact.bizRelResolutionStatus)" :title="editingFact.bizRelResolutionStatus || '-'">{{ resolutionCn(editingFact.bizRelResolutionStatus) }}</span></span>
          </div>
          <div class="rf-form-row"><label>事实覆盖期间：</label><input v-model="editingFact.period" type="text" class="form-input" /></div>
          <div class="rf-form-row"><label>对手源ID：</label><input v-model="editingFact.counterpartySourceId" type="text" class="form-input" /></div>
          <div class="rf-form-row"><label>对手名称：</label><input v-model="editingFact.counterpartyNameSnapshot" type="text" class="form-input" /></div>
          <div class="rf-form-row"><label>往来单位ID：</label><input v-model="editingFact.bizRelEntityId" type="text" class="form-input" /></div>
          <div class="rf-form-row"><label>支付金额：</label><input v-model="editingFact.paymentAmount" type="text" class="form-input" /></div>
          <div class="rf-form-row"><label>期末余额：</label><input v-model="editingFact.endingBalance" type="text" class="form-input" /></div>
          <div class="rf-form-row"><label>期初余额：</label><input v-model="editingFact.openingBalance" type="text" class="form-input" /></div>
          <div class="rf-form-row"><label><span class="required">*</span> 变更原因：</label><input v-model="editChangeReason" type="text" class="form-input" placeholder="必填" /></div>
          <div v-if="editingFact.lifecycleMetadata" class="rf-metadata-section">
            <div class="rf-metadata-title">治理记录 (lifecycleMetadata)</div>
            <pre class="rf-metadata-json">{{ formatMetadata(editingFact.lifecycleMetadata) }}</pre>
          </div>
          <div class="rf-form-actions" style="margin-top:16px">
            <button class="btn btn-primary" @click="submitEdit" :disabled="editSubmitting">{{ editSubmitting ? '保存中...' : '保存' }}</button>
            <button class="btn btn-default" @click="closeEditDrawer">取消</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ========== 治理动作弹窗 ========== -->
    <div v-if="govDialog.open" ref="govOverlay" class="rf-drawer-overlay" @click.self="closeGovernanceDialog">
      <div class="rf-gov-dialog">
        <div class="rf-gov-dialog-header">
          <h3>{{ govDialog.title }}</h3>
          <button class="rf-drawer-close" @click="closeGovernanceDialog"><i class="h-icon-close"></i></button>
        </div>
        <div class="rf-gov-dialog-body">
          <div v-if="govDialog.scopeNote" class="rf-gov-scope-note"><i class="h-icon-info"></i> {{ govDialog.scopeNote }}</div>
          <div class="rf-form-row">
            <label><span class="required">*</span> 变更原因：</label>
            <input v-model="govDialog.changeReason" type="text" class="form-input" placeholder="必填，说明治理操作原因" style="width:100%" />
          </div>
          <div v-if="govDialog.needsSupersededBy" class="rf-form-row">
            <label><span class="required">*</span> 取代事实ID：</label>
            <input v-model="govDialog.supersededByFactId" type="text" class="form-input" placeholder="输入取代此事实的新事实ID" style="width:100%" />
          </div>
          <div v-if="govDialog.needsBizRelEntityId" class="rf-form-row">
            <label><span class="required">*</span> 往来单位ID：</label>
            <input v-model="govDialog.bizRelEntityId" type="text" class="form-input" placeholder="请输入要绑定的往来单位ID" style="width:100%" />
          </div>
          <div class="rf-form-actions" style="margin-top:16px">
            <button class="btn btn-primary" @click="submitGovernanceAction" :disabled="govDialog.submitting">{{ govDialog.submitting ? '执行中...' : '确认' }}</button>
            <button class="btn btn-default" @click="closeGovernanceDialog">取消</button>
          </div>
        </div>
      </div>
    </div>
  </app-content>
</template>

<script>
import {
  getStandardFields,
  autoBuildMapping,
  transformRowsByMapping,
  findUnmappedExcelHeaders
} from './referenceFactFieldAliases.js';

const SAMPLE_PURCHASE_ROWS = JSON.stringify([
  {"年月":"2026-05","供应商ID":"SUP-001","供应商名称":"中山市万升照明有限公司","期初余额":"120000.00","采购入金额":"58000.00","采购入差异金额":"-500.00","退款金额":"2000.00","支付金额":"30000.00","TE5月处置扣除金额":"1200.00","期末余额":"145000.00"},
  {"年月":"2026-05","供应商ID":"SUP-002","供应商名称":"深圳市鸿发电子有限公司","期初余额":"85000.00","采购入金额":"22000.00","采购入差异金额":"0.00","退款金额":"0.00","支付金额":"15000.00","TE5月处置扣除金额":"500.00","期末余额":"91500.00"}
], null, 2);

const SAMPLE_LOGISTICS_ROWS = JSON.stringify([
  {"年月":"2026-05","物流商ID":"LOG-001","物流商名称":"某物流有限公司","期初金额":"10000.00","头程金额":"3000.00","尾程金额":"2000.00","头程差异修正金额":"-100.00","赔偿金额":"300.00","支付金额":"4500.00","期末金额":"10400.00"}
], null, 2);

const RISK_FLAG_CN = {
  REFERENCE_CURRENCY_DEFAULTED: '币种默认',
  REFERENCE_COUNTERPARTY_NOT_MATCHED: '对手方未匹配',
  REFERENCE_COUNTERPARTY_MATCH_FAILED: '关系匹配异常',
  REFERENCE_AMOUNT_ABNORMAL: '金额异常',
  REFERENCE_PERIOD_ABNORMAL: '期间异常',
  REFERENCE_BIZREL_NOT_MATCHED: '关系未匹配',
  REFERENCE_FACT_NOT_FOUND: '未命中参考',
  PAYABLE_FACT_MATCHED: '应付已命中',
  PAYABLE_FACT_CONFLICT: '应付冲突',
  PAYABLE_PARTIAL_MATCH: '应付部分匹配',
  LOGISTICS_PAYABLE_FACT_MATCHED: '物流已命中',
  LOGISTICS_PAYABLE_FACT_CANDIDATE: '物流候选',
  AP_VS_PREPAY_UNCLEAR: '应付预付不明',
  FEE_CATEGORY_UNCLEAR: '费用科目不明'
};

const STATUS_CN = {
  ACTIVE: '可用于建议',
  BLOCKED: '不参与建议',
  IGNORED: '已忽略',
  SUPERSEDED: '已取代',
  ARCHIVED: '已归档',
  DISABLED: '已停用'
};

const RESOLUTION_CN = {
  UNRESOLVED: '未匹配',
  AUTO_RESOLVED: '自动匹配',
  MANUAL_RESOLVED: '手动匹配',
  RESOLUTION_IGNORED: '匹配已忽略'
};

const MATCH_STATUS_CN = { MATCHED: '已匹配', CONFIRMED: '已匹配', NOT_FOUND: '未匹配', CONFLICT: '匹配冲突', ERROR: '匹配异常' };

// risk flag priority: lower number = higher priority (shown first, kept in overflow limit)
const RISK_PRIORITY = {
  REFERENCE_AMOUNT_ABNORMAL: 1,
  REFERENCE_PERIOD_ABNORMAL: 1,
  REFERENCE_BIZREL_NOT_MATCHED: 2,
  REFERENCE_COUNTERPARTY_NOT_MATCHED: 2,
  REFERENCE_COUNTERPARTY_MATCH_FAILED: 2,
  PAYABLE_FACT_CONFLICT: 2,
  AP_VS_PREPAY_UNCLEAR: 3,
  FEE_CATEGORY_UNCLEAR: 3,
  REFERENCE_CURRENCY_DEFAULTED: 4,
  REFERENCE_FACT_NOT_FOUND: 4,
  PAYABLE_FACT_MATCHED: 5,
  PAYABLE_PARTIAL_MATCH: 5,
  LOGISTICS_PAYABLE_FACT_MATCHED: 5,
  LOGISTICS_PAYABLE_FACT_CANDIDATE: 6
};

// governance action definitions
const GOV_ACTIONS = {
  ignore: { label: '忽略此事实', title: '忽略参考事实', needsReason: true },
  restore: { label: '恢复为可用', title: '恢复参考事实为可用', needsReason: true },
  supersede: { label: '标记为被取代', title: '标记参考事实为被取代', needsReason: true, needsSupersededBy: true },
  archive: { label: '归档', title: '归档参考事实', needsReason: true },
  resolveBizrel: { label: '手动绑定往来单位', title: '手动绑定往来单位', needsReason: true, needsBizRelEntityId: true },
  clearBizrelResolution: { label: '清除往来单位匹配', title: '清除往来单位匹配', needsReason: true },
  ignoreBizrelResolution: { label: '忽略往来单位匹配', title: '忽略往来单位匹配', needsReason: true },
  rematchBizrel: { label: '重新匹配往来单位', title: '重新匹配往来单位', needsReason: true },
  archiveBatch: { label: '归档整个批次', title: '归档整个批次', needsReason: true, scopeNote: '此操作将归档该批次下所有非已归档状态的参考事实。归档后不会参与后续银行流水判断，但历史记录仍保留。' },
  ignoreBatch: { label: '批量忽略', title: '批量忽略', needsReason: true, scopeNote: '此操作将对当前批次下所有 ACTIVE / BLOCKED 状态的参考事实执行忽略操作。操作基于整个导入批次。' },
  rematchBizrelBatch: { label: '批量重新匹配往来单位', title: '批量重新匹配往来单位', needsReason: true, scopeNote: '此操作将对当前批次下所有可重新匹配的参考事实执行往来单位重新匹配。已手动匹配和已忽略匹配的记录将被跳过。操作基于整个导入批次。操作完成后将显示匹配数和跳过数。' }
};

export default {
  name: 'ReferenceFactImport',
  data() {
    return {
      activeTab: 'query',
      importing: false, querying: false,
      importForm: { factType: 'PURCHASE_PAYABLE_SUMMARY', period: '2026-05', importBatchId: '', defaultCurrency: '', importMode: 'excel', jsonText: '', excelFileName: '', excelFile: null },
      importResult: null,
      queryForm: { period: '', factType: '', counterpartyRole: '', counterpartyName: '', counterpartySourceId: '', bizRelEntityId: '', status: 'ACTIVE', resolution: '' },
      batchForm: { importBatchId: '', status: 'ACTIVE', resolution: '' },
      queryResult: null,
      queryPager: { page: 1, limit: 20, totalPages: 1 },
      recentBatches: [],
      loadingRecentBatches: false,
      moreMenuIndex: -1,
      editDrawerOpen: false,
      editingFact: {},
      editChangeReason: '',
      editSubmitting: false,
      // flexible import state
      flexibleStep: 'upload',
      flexibleFactType: 'PURCHASE_PAYABLE_SUMMARY',
      flexibleFile: null,
      flexibleFileName: '',
      flexibleHeaders: [],
      flexibleRawRows: [],
      flexibleTotalRows: 0,
      flexibleMapping: [],
      flexibleUnmappedHeaders: [],
      flexiblePreviewRows: [],
      flexibleValidationErrors: [],
      flexibleSubmitting: false,
      flexibleFileLoading: false,
      govDialog: {
        open: false,
        key: '',
        title: '',
        scopeNote: '',
        needsSupersededBy: false,
        needsBizRelEntityId: false,
        factId: null,
        changeReason: '',
        supersededByFactId: '',
        bizRelEntityId: '',
        submitting: false
      }
    };
  },
  created() {
    this.loadRecentBatches();
  },
  mounted() {
    this._docClick = (e) => {
      if (this.moreMenuIndex >= 0 && !e.target.closest('.rf-more-wrap')) {
        this.moreMenuIndex = -1;
      }
    };
    document.addEventListener('click', this._docClick);
  },
  beforeDestroy() {
    const el = this.$refs.drawerOverlay;
    if (el && el.parentNode) {
      el.parentNode.removeChild(el);
    }
    const govEl = this.$refs.govOverlay;
    if (govEl && govEl.parentNode) {
      govEl.parentNode.removeChild(govEl);
    }
    if (this._docClick) {
      document.removeEventListener('click', this._docClick);
    }
  },
  methods: {
    handleExcelFileChange(event) {
      const file = event.target.files && event.target.files[0];
      if (file) { this.importForm.excelFile = file; this.importForm.excelFileName = file.name; }
      else { this.importForm.excelFile = null; this.importForm.excelFileName = ''; }
    },
    async handleImport() { await (this.importForm.importMode === 'excel' ? this.handleExcelImport() : this.handleJsonImport()); },
    async handleJsonImport() {
      const t = (this.importForm.jsonText || '').trim();
      if (!t) { this.$Message.warning('请输入JSON数据'); return; }
      let rows; try { rows = JSON.parse(t); if (!Array.isArray(rows)) rows = [rows]; } catch (e) { this.$Message.error('JSON格式解析失败: ' + e.message); return; }
      await this.doImport({ sourceFileId: 'manual-' + Date.now(), importBatchId: this.importForm.importBatchId || '', defaultCurrency: this.importForm.defaultCurrency || '', rows });
    },
    async handleExcelImport() {
      if (!this.importForm.excelFile) { this.$Message.warning('请选择 Excel 文件'); return; }
      const fd = new FormData(); fd.append('file', this.importForm.excelFile);
      if (this.importForm.period) fd.append('period', this.importForm.period);
      if (this.importForm.importBatchId) fd.append('importBatchId', this.importForm.importBatchId);
      if (this.importForm.defaultCurrency) fd.append('defaultCurrency', this.importForm.defaultCurrency);
      this.importing = true; this.importResult = null;
      try {
        let r = this.importForm.factType === 'PURCHASE_PAYABLE_SUMMARY' ? await this.$api.referenceFact.importPurchasePayableExcel(fd) : await this.$api.referenceFact.importLogisticsPayableExcel(fd);
        if (r && r.code === 200) { this.importResult = r.data || r; this.$Message.success('Excel 导入完成'); } else { this.$Message.error((r && r.msg) || '导入失败'); }
      } catch (e) { this.$Message.error('Excel导入失败: ' + (e.message || '网络错误')); } finally { this.importing = false; }
    },
    async doImport(body) {
      this.importing = true; this.importResult = null;
      try {
        let r = this.importForm.factType === 'PURCHASE_PAYABLE_SUMMARY' ? await this.$api.referenceFact.importPurchasePayable(body) : await this.$api.referenceFact.importLogisticsPayable(body);
        if (r && r.code === 200) { this.importResult = r.data || r; this.$Message.success('导入完成'); } else { this.$Message.error((r && r.msg) || '导入失败'); }
      } catch (e) { this.$Message.error('导入失败: ' + (e.message || '网络错误')); } finally { this.importing = false; }
    },
    buildQueryParams() {
      const p = {};
      if (this.queryForm.period) p.period = this.queryForm.period;
      if (this.queryForm.factType) p.factType = this.queryForm.factType;
      if (this.queryForm.counterpartyRole) p.counterpartyRole = this.queryForm.counterpartyRole;
      if (this.queryForm.counterpartyName) p.counterpartyName = this.queryForm.counterpartyName;
      if (this.queryForm.counterpartySourceId) p.counterpartySourceId = this.queryForm.counterpartySourceId;
      if (this.queryForm.bizRelEntityId) p.bizRelEntityId = this.queryForm.bizRelEntityId;
      if (this.queryForm.status && this.queryForm.status !== 'ALL') p.status = this.queryForm.status;
      if (this.queryForm.resolution) p.resolution = this.queryForm.resolution;
      return p;
    },
    resetQueryForm() {
      this.queryForm = { period: '', factType: '', counterpartyRole: '', counterpartyName: '', counterpartySourceId: '', bizRelEntityId: '', status: 'ACTIVE', resolution: '' };
      this.queryPager.page = 1;
    },
    goPage(page) { this.queryPager.page = page; this.activeTab === 'batch' ? this.handleBatchQuery() : this.fetchQueryFacts(); },
    changePageSize() { this.queryPager.page = 1; this.activeTab === 'batch' ? this.handleBatchQuery() : this.fetchQueryFacts(); },
    async handleQuery() {
      this.queryPager.page = 1;
      await this.fetchQueryFacts();
    },
    async fetchQueryFacts() {
      const p = this.buildQueryParams();
      p.page = this.queryPager.page || 1;
      p.limit = this.queryPager.limit || 20;
      this.querying = true;
      try {
        let r = await this.$api.referenceFact.queryReferenceFacts(p);
        if (r && r.code === 200) {
          this.queryResult = r.data || r;
          const total = this.queryResult.totalRows || 0;
          this.queryPager.totalPages = Math.max(1, Math.ceil(total / this.queryPager.limit));
        } else { this.$Message.error((r && r.msg) || '查询失败'); }
      } catch (e) { this.$Message.error('查询失败'); } finally { this.querying = false; }
    },
    async handleBatchQuery() {
      if (!this.batchForm.importBatchId.trim()) { this.$Message.warning('请输入导入批次ID'); return; }
      this.querying = true;
      try {
        const params = {};
        if (this.batchForm.status && this.batchForm.status !== 'ALL') params.status = this.batchForm.status;
        if (this.batchForm.resolution) params.resolution = this.batchForm.resolution;
        let r = await this.$api.referenceFact.queryByImportBatchId(this.batchForm.importBatchId.trim(), params);
        if (r && r.code === 200) {
          this.queryResult = r.data || r;
          this.queryPager.totalPages = 1;
          this.queryPager.page = 1;
        } else this.$Message.error((r && r.msg) || '查询失败');
      } catch (e) { this.$Message.error('查询失败'); } finally { this.querying = false; }
    },
    async loadRecentBatches() {
      this.loadingRecentBatches = true;
      try {
        const r = await this.$api.referenceFact.listImportBatches({ status: 'ALL', limit: 12 });
        if (r && r.code === 200) {
          const data = r.data || r;
          this.recentBatches = data.batches || [];
        }
      } catch (e) {
        this.recentBatches = [];
      } finally {
        this.loadingRecentBatches = false;
      }
    },
    selectRecentBatch(batch) {
      this.batchForm.importBatchId = batch.importBatchId || '';
      this.activeTab = 'batch';
      this.handleBatchQuery();
    },
    queryBatchResult(id) { this.activeTab = 'batch'; this.batchForm.importBatchId = id || ''; this.handleBatchQuery(); },
    fillSamplePurchaseRows() { this.importForm.factType = 'PURCHASE_PAYABLE_SUMMARY'; this.importForm.jsonText = SAMPLE_PURCHASE_ROWS; },
    fillSampleLogisticsRows() { this.importForm.factType = 'LOGISTICS_PAYABLE_SUMMARY'; this.importForm.jsonText = SAMPLE_LOGISTICS_ROWS; },
    factTypeFull(t) { const m = { PURCHASE_PAYABLE_SUMMARY: '采购应付汇总', LOGISTICS_PAYABLE_SUMMARY: '物流应付明细汇总' }; return m[t] || t; },
    factTypeShort(t) { const m = { PURCHASE_PAYABLE_SUMMARY: '采购应付', LOGISTICS_PAYABLE_SUMMARY: '物流应付' }; return m[t] || t; },
    formatPeriod(p) { if (!p) return '-'; if (/^\d{6}$/.test(p)) return p.slice(0,4) + '-' + p.slice(4,6); return p; },
    matchStatusCn(s) { return MATCH_STATUS_CN[s] || s || '-'; },
    matchStatusClass(s) { return 'match-' + String(s || '').toLowerCase(); },
    statusCn(s) { return STATUS_CN[s] || s || '-'; },
    statusClass(s) { return 'st-' + String(s || '').toLowerCase(); },
    resolutionCn(s) { return RESOLUTION_CN[s] || s || '-'; },
    resolutionClass(s) { return 'res-' + String(s || '').toLowerCase(); },
    fmt(v) { const n = Number(v); return Number.isNaN(n) ? String(v) : n.toLocaleString('zh-CN', { minimumFractionDigits:2, maximumFractionDigits:2 }); },
    formatMetadata(raw) {
      if (!raw) return '';
      try { return JSON.stringify(JSON.parse(raw), null, 2); } catch (e) { return raw; }
    },
    parseRiskFlags(flags) {
      if (!flags) return [];
      let rawFlags = flags;
      if (typeof flags === 'string') {
        try {
          rawFlags = JSON.parse(flags);
        } catch (e) {
          rawFlags = flags.split(',').map(flag => flag && flag.trim()).filter(Boolean);
        }
      }
      return (Array.isArray(rawFlags) ? rawFlags : [rawFlags])
        .filter(Boolean)
        .map(flag => ({ raw: flag, cn: RISK_FLAG_CN[flag] || '其他提示' }));
    },
    hasRiskFlags(flags) { return this.parseRiskFlags(flags).length > 0; },
    refreshCurrentView() { return this.activeTab === 'batch' ? this.handleBatchQuery() : this.fetchQueryFacts(); },

    // ----- more menu -----
    toggleMoreMenu(index) { this.moreMenuIndex = this.moreMenuIndex === index ? -1 : index; },

    // ----- listingActions: 根据当前status/resolution返回可用治理动作 -----
    listingActions(fact) {
      const actions = [];
      const s = fact.status;
      const r = fact.bizRelResolutionStatus;

      // lifecycle actions
      if (s === 'ACTIVE' || s === 'BLOCKED') {
        actions.push(GOV_ACTIONS.ignore);
      }
      if (s === 'IGNORED' || s === 'BLOCKED') {
        actions.push(GOV_ACTIONS.restore);
      }
      if (s === 'ACTIVE' || s === 'IGNORED' || s === 'BLOCKED') {
        actions.push(GOV_ACTIONS.supersede);
      }
      if (s !== 'ARCHIVED') {
        actions.push(GOV_ACTIONS.archive);
      }

      // BizRel actions — only for non-archived facts
      if (s !== 'ARCHIVED') {
        if (r === 'UNRESOLVED' || r === 'AUTO_RESOLVED') {
          actions.push(GOV_ACTIONS.ignoreBizrelResolution);
        }
        if (r === 'AUTO_RESOLVED' || r === 'MANUAL_RESOLVED' || r === 'RESOLUTION_IGNORED') {
          actions.push(GOV_ACTIONS.clearBizrelResolution);
        }
        if (r !== 'MANUAL_RESOLVED' && r !== 'RESOLUTION_IGNORED') {
          actions.push(GOV_ACTIONS.rematchBizrel);
        }
        actions.push(GOV_ACTIONS.resolveBizrel);
      }

      return actions;
    },

    // ----- risk flags (sorted, max 2 + overflow) -----
    sortedRiskFlags(flags) {
      const parsed = this.parseRiskFlags(flags);
      return parsed.sort((a, b) => {
        const pa = RISK_PRIORITY[a.raw] != null ? RISK_PRIORITY[a.raw] : 99;
        const pb = RISK_PRIORITY[b.raw] != null ? RISK_PRIORITY[b.raw] : 99;
        return pa - pb;
      });
    },
    visibleRiskFlags(flags) { return this.sortedRiskFlags(flags).slice(0, 2); },
    overflowRiskCount(flags) { return Math.max(0, this.sortedRiskFlags(flags).length - 2); },
    allRiskFlagsTitle(flags) { return this.sortedRiskFlags(flags).map(f => f.cn).join('、'); },
    riskChipClass(code) {
      const p = RISK_PRIORITY[code];
      if (p === 1) return 'rf-chip-danger';
      if (p === 2) return 'rf-chip-warn';
      return 'rf-chip-info';
    },
    isNegative(v) {
      if (v === null || v === undefined || v === '') return false;
      return Number(v) < 0;
    },
    isPrimaryRisk(code) {
      const p = RISK_PRIORITY[code];
      return p != null && p <= 2;
    },

    // ----- edit drawer -----
    openEditDrawer(fact) {
      this.editingFact = { ...fact };
      this.editChangeReason = '';
      this.editSubmitting = false;
      this.editDrawerOpen = true;
      this.$nextTick(() => {
        const el = this.$refs.drawerOverlay;
        if (el && el.parentNode !== document.body) {
          document.body.appendChild(el);
        }
      });
    },
    closeEditDrawer() {
      const el = this.$refs.drawerOverlay;
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
      this.editDrawerOpen = false;
      this.editingFact = {};
      this.editChangeReason = '';
    },
    async submitEdit() {
      if (!this.editChangeReason.trim()) { this.$Message.warning('请填写变更原因'); return; }
      this.editSubmitting = true;
      try {
        const body = { ...this.editingFact, changeReason: this.editChangeReason }; delete body.id;
        let r = await this.$api.referenceFact.updateFact(this.editingFact.id, body);
        if (r && r.code === 200) { this.$Message.success('保存成功'); this.closeEditDrawer(); await this.refreshCurrentView(); }
        else this.$Message.error((r && r.msg) || '保存失败');
      } catch (e) { this.$Message.error('保存失败'); } finally { this.editSubmitting = false; }
    },

    // ----- flexible import -----
    switchImportMode(mode) {
      if (mode === 'flexible') {
        this.resetFlexibleImport();
      }
      this.importForm.importMode = mode;
    },
    handleFlexibleFileChange(event) {
      const file = event.target.files && event.target.files[0];
      if (file) { this.flexibleFile = file; this.flexibleFileName = file.name; }
      else { this.flexibleFile = null; this.flexibleFileName = ''; }
    },
    async loadFlexibleData() {
      if (!this.flexibleFile) { this.$Message.warning('请选择 Excel 文件'); return; }
      this.flexibleFileLoading = true;
      try {
        const fd = new FormData();
        fd.append('file', this.flexibleFile);
        const r = await this.$api.referenceFact.readExcelData(fd);
        if (r && r.code === 200) {
          const data = r.data || r;
          this.flexibleHeaders = data.headers || [];
          this.flexibleRawRows = data.rows || [];
          this.flexibleTotalRows = data.totalRows || 0;
          if (this.flexibleHeaders.length === 0) {
            this.$Message.warning('无法读取 Excel 表头，请检查文件格式');
            return;
          }
          if (this.flexibleRawRows.length === 0) {
            this.$Message.warning('Excel 文件无数据行');
            return;
          }
          this.buildFlexibleMapping();
          this.flexibleStep = 'mapping';
          this.$Message.success('已读取 ' + this.flexibleTotalRows + ' 行数据');
        } else {
          this.$Message.error((r && r.msg) || '读取 Excel 失败');
        }
      } catch (e) {
        this.$Message.error('读取 Excel 文件失败: ' + (e.message || '网络错误'));
      } finally {
        this.flexibleFileLoading = false;
      }
    },
    buildFlexibleMapping() {
      this.flexibleMapping = autoBuildMapping(this.flexibleHeaders, this.flexibleFactType);
      this.flexibleUnmappedHeaders = findUnmappedExcelHeaders(this.flexibleHeaders, this.flexibleMapping);
    },
    isRequiredField(key) {
      const fields = getStandardFields(this.flexibleFactType);
      const field = fields.find(f => f.key === key);
      return field ? field.required : false;
    },
    previewFlexibleMapping() {
      // validate required fields
      const errors = [];
      for (const item of this.flexibleMapping) {
        if (this.isRequiredField(item.standardKey) && !item.excelHeader) {
          errors.push('必填字段「' + item.standardKey + '」未配置映射');
        }
      }
      this.flexibleValidationErrors = errors;

      // compute preview rows (first 5)
      const mapped = this.flexibleMapping.filter(m => m.excelHeader);
      if (mapped.length === 0) {
        this.$Message.warning('至少需要映射一个字段');
        return;
      }
      this.flexiblePreviewRows = transformRowsByMapping(this.flexibleMapping, this.flexibleRawRows.slice(0, 5));
      this.flexibleStep = 'preview';
    },
    backToFlexibleMapping() {
      this.flexibleStep = 'mapping';
    },
    resetFlexibleImport() {
      this.flexibleStep = 'upload';
      this.flexibleFile = null;
      this.flexibleFileName = '';
      this.flexibleHeaders = [];
      this.flexibleRawRows = [];
      this.flexibleTotalRows = 0;
      this.flexibleMapping = [];
      this.flexibleUnmappedHeaders = [];
      this.flexiblePreviewRows = [];
      this.flexibleValidationErrors = [];
      this.flexibleSubmitting = false;
      this.flexibleFileLoading = false;
    },
    async submitFlexibleImport() {
      if (this.flexibleValidationErrors.length > 0) {
        this.$Message.warning('请先修复映射问题再导入');
        return;
      }
      const mappedFields = this.flexibleMapping.filter(m => m.excelHeader);
      if (mappedFields.length === 0) {
        this.$Message.warning('至少需要映射一个字段');
        return;
      }
      const requiredMapped = mappedFields.filter(m => this.isRequiredField(m.standardKey));
      if (requiredMapped.length === 0) {
        this.$Message.warning('至少需要映射一个必填字段（年月/名称）');
        return;
      }

      this.flexibleSubmitting = true;
      try {
        // transform all rows
        const transformedRows = transformRowsByMapping(this.flexibleMapping, this.flexibleRawRows);
        if (transformedRows.length === 0) {
          this.$Message.warning('转换后无数据行');
          return;
        }

        const body = {
          sourceFileId: this.flexibleFileName || 'flexible-' + Date.now(),
          defaultCurrency: this.importForm.defaultCurrency || '',
          rows: transformedRows
        };

        let r;
        if (this.flexibleFactType === 'PURCHASE_PAYABLE_SUMMARY') {
          r = await this.$api.referenceFact.importPurchasePayable(body);
        } else {
          r = await this.$api.referenceFact.importLogisticsPayable(body);
        }

        if (r && r.code === 200) {
          this.importResult = r.data || r;
          this.$Message.success('灵活导入完成');
          this.resetFlexibleImport();
        } else {
          this.$Message.error((r && r.msg) || '导入失败');
        }
      } catch (e) {
        this.$Message.error('导入失败: ' + (e.message || '网络错误'));
      } finally {
        this.flexibleSubmitting = false;
      }
    },
    // 预览时只展示有映射的列
    visibleMappingColumns() {
      return this.flexibleMapping.filter(m => m.excelHeader);
    },

    // ----- governance dialog -----
    openGovernanceDialog(actionKey, fact) {
      const def = GOV_ACTIONS[actionKey];
      if (!def) return;

      this.govDialog = {
        open: true,
        key: actionKey,
        title: def.title || def.label,
        scopeNote: def.scopeNote || '',
        needsSupersededBy: !!def.needsSupersededBy,
        needsBizRelEntityId: !!def.needsBizRelEntityId,
        factId: fact ? fact.id : null,
        fact: fact || null,
        changeReason: '',
        supersededByFactId: '',
        bizRelEntityId: fact ? (fact.bizRelEntityId || '') : '',
        submitting: false
      };

      this.$nextTick(() => {
        const el = this.$refs.govOverlay;
        if (el && el.parentNode !== document.body) {
          document.body.appendChild(el);
        }
      });
    },
    closeGovernanceDialog() {
      const el = this.$refs.govOverlay;
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
      this.govDialog.open = false;
    },
    async submitGovernanceAction() {
      if (!this.govDialog.changeReason.trim()) { this.$Message.warning('请填写变更原因'); return; }
      const key = this.govDialog.key;
      const factId = this.govDialog.factId;

      // validate extra fields
      if (this.govDialog.needsSupersededBy && !this.govDialog.supersededByFactId.trim()) {
        this.$Message.warning('请填写取代事实ID'); return;
      }
      if (this.govDialog.needsBizRelEntityId && !this.govDialog.bizRelEntityId.trim()) {
        this.$Message.warning('请填写往来单位ID'); return;
      }

      this.govDialog.submitting = true;
      try {
        let res;

        if (key === 'archiveBatch') {
          const body = { changeReason: this.govDialog.changeReason.trim() };
          res = await this.$api.referenceFact.archiveBatch(this.batchForm.importBatchId, body);
          if (res && res.code === 200) {
            const data = res.data || res;
            this.$Message.success('批次归档完成，共归档 ' + (data.archivedCount || 0) + ' 条');
            this.batchForm.status = 'ALL';
            await this.handleBatchQuery();
            await this.loadRecentBatches();
          } else { this.$Message.error((res && res.msg) || '批次归档失败'); }
        } else if (key === 'ignoreBatch') {
          const body = { changeReason: this.govDialog.changeReason.trim() };
          res = await this.$api.referenceFact.ignoreBatch(this.batchForm.importBatchId, body);
          if (res && res.code === 200) {
            const data = res.data || res;
            this.$Message.success('批量忽略完成，共忽略 ' + (data.ignoredCount || 0) + ' 条');
            await this.handleBatchQuery();
            await this.loadRecentBatches();
          } else { this.$Message.error((res && res.msg) || '批量忽略失败'); }
        } else if (key === 'rematchBizrelBatch') {
          const body = { changeReason: this.govDialog.changeReason.trim() };
          res = await this.$api.referenceFact.rematchBizrelBatch(this.batchForm.importBatchId, body);
          if (res && res.code === 200) {
            const data = res.data || res;
            this.$Message.success('批量重新匹配完成：匹配 ' + (data.rematchedCount || 0) + ' 条，跳过 ' + (data.skippedCount || 0) + ' 条');
            await this.handleBatchQuery();
          } else { this.$Message.error((res && res.msg) || '批量重新匹配失败'); }
        } else if (key === 'ignore') {
          res = await this.$api.referenceFact.ignoreFact(factId, { changeReason: this.govDialog.changeReason.trim() });
          if (res && res.code === 200) { this.$Message.success('已忽略'); await this.refreshCurrentView(); }
          else this.$Message.error((res && res.msg) || '忽略失败');
        } else if (key === 'restore') {
          res = await this.$api.referenceFact.restoreFact(factId, { changeReason: this.govDialog.changeReason.trim() });
          if (res && res.code === 200) { this.$Message.success('已恢复为可用'); await this.refreshCurrentView(); }
          else this.$Message.error((res && res.msg) || '恢复失败');
        } else if (key === 'supersede') {
          res = await this.$api.referenceFact.supersedeFact(factId, {
            changeReason: this.govDialog.changeReason.trim(),
            supersededByFactId: Number(this.govDialog.supersededByFactId.trim())
          });
          if (res && res.code === 200) { this.$Message.success('已标记为被取代'); await this.refreshCurrentView(); }
          else this.$Message.error((res && res.msg) || '取代操作失败');
        } else if (key === 'archive') {
          res = await this.$api.referenceFact.archiveFact(factId, { changeReason: this.govDialog.changeReason.trim() });
          if (res && res.code === 200) { this.$Message.success('已归档'); await this.refreshCurrentView(); }
          else this.$Message.error((res && res.msg) || '归档失败');
        } else if (key === 'resolveBizrel') {
          res = await this.$api.referenceFact.resolveBizrel(factId, {
            changeReason: this.govDialog.changeReason.trim(),
            bizRelEntityId: this.govDialog.bizRelEntityId.trim()
          });
          if (res && res.code === 200) { this.$Message.success('往来单位手动绑定完成'); await this.refreshCurrentView(); }
          else this.$Message.error((res && res.msg) || '往来单位绑定失败');
        } else if (key === 'clearBizrelResolution') {
          res = await this.$api.referenceFact.clearBizrelResolution(factId, { changeReason: this.govDialog.changeReason.trim() });
          if (res && res.code === 200) { this.$Message.success('往来单位匹配已清除'); await this.refreshCurrentView(); }
          else this.$Message.error((res && res.msg) || '清除往来单位匹配失败');
        } else if (key === 'ignoreBizrelResolution') {
          res = await this.$api.referenceFact.ignoreBizrelResolution(factId, { changeReason: this.govDialog.changeReason.trim() });
          if (res && res.code === 200) { this.$Message.success('往来单位匹配已忽略'); await this.refreshCurrentView(); }
          else this.$Message.error((res && res.msg) || '忽略往来单位匹配失败');
        } else if (key === 'rematchBizrel') {
          res = await this.$api.referenceFact.rematchBizrel(factId, { changeReason: this.govDialog.changeReason.trim() });
          if (res && res.code === 200) { this.$Message.success('往来单位重新匹配完成'); await this.refreshCurrentView(); }
          else this.$Message.error((res && res.msg) || '重新匹配失败');
        }
      } catch (e) {
        this.$Message.error('操作失败: ' + (e.message || '网络错误'));
      } finally {
        this.govDialog.submitting = false;
        this.closeGovernanceDialog();
      }
    }
  }
};
</script>

<style scoped>
.reference-fact-page { max-width: 100%; }

.rf-notice { display: flex; align-items: flex-start; gap: 10px; padding: 12px 16px; background: #f0f7ff; border: 1px solid #b3d8ff; border-radius: 4px; margin-bottom: 20px; font-size: 13px; color: #409eff; line-height: 1.6; }
.rf-tabs { display: flex; gap: 0; margin-bottom: 20px; border-bottom: 2px solid #e4e7ed; }
.rf-tab-btn { padding: 10px 20px; border: none; background: none; font-size: 14px; color: #606266; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px; transition: all 0.2s; }
.rf-tab-btn.active { color: #409eff; border-bottom-color: #409eff; font-weight: 500; }
.rf-tab-btn:hover { color: #409eff; }
.rf-panel { margin-bottom: 24px; }
.rf-form { padding: 20px; background: #fafbfc; border: 1px solid #e4e7ed; border-radius: 4px; }
.rf-form-row { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; font-size: 13px; }
.rf-form-row label { min-width: 110px; color: #606266; text-align: right; }
.required { color: #f56c6c; }
.form-input { padding: 6px 10px; border: 1px solid #dcdfe6; border-radius: 4px; font-size: 13px; min-width: 220px; }
.rf-json-input { width: 100%; min-height: 200px; padding: 12px; border: 1px solid #dcdfe6; border-radius: 4px; font-family: 'Menlo', 'Monaco', monospace; font-size: 12px; line-height: 1.5; resize: vertical; margin-bottom: 12px; }
.rf-form-actions { display: flex; gap: 8px; align-items: center; }
.rf-import-mode-switch { display: flex; gap: 0; border: 1px solid #dcdfe6; border-radius: 4px; overflow: hidden; }
.rf-mode-btn { padding: 6px 16px; border: none; background: #fff; font-size: 13px; color: #606266; cursor: pointer; transition: all 0.2s; }
.rf-mode-btn.active { background: #409eff; color: #fff; }
.rf-mode-btn:hover:not(.active) { background: #f5f7fa; }
.rf-excel-upload { margin-bottom: 12px; }
.rf-excel-name { font-size: 12px; color: #67c23a; }
.rf-result { margin-top: 20px; padding: 16px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 4px; }
.rf-result-header { display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600; color: #16a34a; margin-bottom: 12px; }
.rf-result-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 12px; }
.rf-result-item { text-align: center; }
.rf-result-item .rf-result-label { display: block; font-size: 12px; color: #909399; margin-bottom: 4px; }
.rf-result-item .rf-result-value { display: block; font-size: 20px; font-weight: 600; color: #303133; }
.rf-result-item .rf-result-value.mono { font-size: 12px; font-family: monospace; }
.rf-result-item.success .rf-result-value { color: #16a34a; } .rf-result-item.failed .rf-result-value { color: #f56c6c; }
.rf-subtitle { font-size: 13px; font-weight: 600; color: #303133; margin-bottom: 6px; }
.rf-warning-item { font-size: 12px; color: #e6a23c; margin-bottom: 2px; } .rf-error-item { font-size: 12px; color: #f56c6c; margin-bottom: 2px; }
.rf-import-warnings, .rf-import-errors { margin-bottom: 10px; }
.rf-result-actions { margin-top: 8px; }
.query-form { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px 18px; align-items: end; }
.query-form .rf-form-row, .rf-batch-query-form .rf-form-row { flex-direction: column; align-items: flex-start; margin-bottom: 0; gap: 6px; }
.query-form .rf-form-row label, .rf-batch-query-form .rf-form-row label { min-width: 0; text-align: left; }
.query-form .form-input, .rf-batch-query-form .form-input { width: 100%; min-width: 0; }
.rf-query-actions { display: flex; align-items: center; gap: 8px; }
.rf-fact-list { margin-top: 16px; }
.rf-fact-list-header { font-size: 13px; color: #909399; margin-bottom: 10px; display: flex; align-items: center; flex-wrap: wrap; }
.rf-empty { padding: 30px; text-align: center; color: #909399; font-size: 14px; }
.rf-batch-layout { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(320px, 0.9fr); gap: 16px; margin-bottom: 16px; align-items: start; }
.rf-batch-query-form { margin: 0; }
.rf-batch-sidebar { padding: 16px; background: #fafbfc; border: 1px solid #e4e7ed; border-radius: 4px; }
.rf-batch-sidebar-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; font-size: 13px; color: #606266; font-weight: 500; }
.rf-batch-list { display: flex; flex-direction: column; gap: 10px; max-height: 360px; overflow-y: auto; }
.rf-batch-card { width: 100%; text-align: left; border: 1px solid #e4e7ed; background: #fff; border-radius: 4px; padding: 12px; cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; }
.rf-batch-card:hover { border-color: #409eff; box-shadow: 0 4px 14px rgba(64, 158, 255, 0.12); }
.rf-batch-card-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.rf-batch-card-id { font-size: 12px; color: #303133; font-family: monospace; }
.rf-batch-card-count { font-size: 12px; color: #909399; white-space: nowrap; }
.rf-batch-card-meta { font-size: 12px; color: #909399; line-height: 1.5; }
.rf-batch-empty { padding: 24px 12px; text-align: center; color: #909399; font-size: 13px; }

/* ---- table ---- */
.rf-table-wrapper { width: 100%; overflow-x: auto; padding-bottom: 4px; }
.rf-table { width: 100%; min-width: 1150px; border-collapse: collapse; font-size: 13px; table-layout: fixed; }
.rf-table th { padding: 8px 6px; background: #f5f7fa; border-bottom: 1px solid #ebeef5; font-weight: 500; color: #606266; vertical-align: middle; font-size: 12px; }
.rf-table td { padding: 7px 6px; border-bottom: 1px solid #ebeef5; color: #303133; vertical-align: middle; white-space: nowrap; word-break: keep-all; overflow-wrap: normal; }
.rf-table td.rf-counterparty, .rf-table td.rf-risk-cell { white-space: normal; }
.rf-table tr:hover td { background: #f5f7fa; }
.rf-nowrap { white-space: nowrap; }
.rf-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; font-size: 13px; }
.rf-num-neg { color: #dc2626; }
.rf-counterparty { max-width: 280px; line-height: 1.45; vertical-align: middle; }
.rf-cp-name { font-size: 13px; color: #1f2937; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rf-cp-sub { font-size: 11px; color: #b0b7c3; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: monospace; }
.rf-bizrel-cell { min-width: 0; }
.rf-risk-cell { min-width: 0; }

/* ---- bizrel text status ---- */
.rf-bizrel-text { font-size: 12px; font-weight: 500; }
.rf-bizrel-text.match-matched, .rf-bizrel-text.match-confirmed { color: #16a34a; }
.rf-bizrel-text.match-not_found { color: #b0b7c3; }
.rf-bizrel-text.match-conflict { color: #e6a23c; }
.rf-bizrel-text.match-error { color: #f56c6c; }

/* ---- actions ---- */
.rf-th-actions { text-align: right; }
.rf-actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; white-space: nowrap; }
.rf-act-btn { padding: 0; border: none; background: none; font-size: 13px; cursor: pointer; }
.rf-act-edit { color: #409eff; }
.rf-act-edit:hover { color: #337ecc; text-decoration: underline; }
.rf-act-more { color: #c0c4cc; font-size: 14px; letter-spacing: 1px; line-height: 1; }
.rf-act-more:hover { color: #909399; }
.rf-more-wrap { position: relative; display: inline-flex; }
.rf-more-menu { position: absolute; top: 100%; right: 0; z-index: 100; margin-top: 4px; background: #fff; border: 1px solid #e4e7ed; border-radius: 6px; box-shadow: 0 4px 16px rgba(15,23,42,0.12); padding: 4px; min-width: 160px; max-height: 280px; overflow-y: auto; }
.rf-more-item { display: block; width: 100%; text-align: left; padding: 6px 12px; border: none; background: none; font-size: 13px; color: #303133; border-radius: 4px; cursor: pointer; }
.rf-more-item:hover { background: #f5f7fa; }
.rf-more-item-disabled { color: #c0c4cc; cursor: default; }
.rf-more-item-disabled:hover { background: none; }

.rf-pagination { display: flex; align-items: center; justify-content: flex-end; gap: 12px; margin-top: 12px; padding-top: 12px; border-top: 1px solid #ebeef5; }
.rf-page-info { font-size: 13px; color: #909399; }
.rf-page-size { min-width: 92px; }

/* ---- unified badges ---- */
.rf-badge { display: inline-block; font-size: 12px; padding: 2px 10px; border-radius: 10px; line-height: 18px; white-space: nowrap; font-weight: 500; }

/* bizrel match */
.rf-badge.match-matched, .rf-badge.match-confirmed { background: #dcfce7; color: #166534; }
.rf-badge.match-not_found { background: #f3f4f6; color: #6b7280; }
.rf-badge.match-conflict { background: #fef3c7; color: #92400e; }
.rf-badge.match-error { background: #fee2e2; color: #991b1b; }

/* fact lifecycle status */
.rf-badge.st-active { background: #dcfce7; color: #166534; }
.rf-badge.st-archived { background: #f3f4f6; color: #6b7280; }
.rf-badge.st-blocked { background: #fee2e2; color: #991b1b; }
.rf-badge.st-ignored { background: #fef3c7; color: #92400e; }
.rf-badge.st-superseded { background: #ede9fe; color: #5b21b6; }

/* bizrel resolution status */
.rf-badge.res-unresolved { background: #f3f4f6; color: #6b7280; }
.rf-badge.res-auto_resolved { background: #dbeafe; color: #1e40af; }
.rf-badge.res-manual_resolved { background: #dcfce7; color: #166534; }
.rf-badge.res-resolution_ignored { background: #fef3c7; color: #92400e; }

.rf-type-tag { display: inline-block; padding: 2px 8px; background: #ecf5ff; color: #409eff; border-radius: 10px; font-size: 12px; white-space: nowrap; }
.rf-entity-id { font-size: 11px; color: #909399; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; }

/* ---- risk chips ---- */
.rf-risk-chips { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }
.rf-risk-chip { display: inline-block; padding: 2px 7px; border-radius: 4px; font-size: 11px; line-height: 16px; white-space: nowrap; cursor: default; font-weight: 500; }
.rf-chip-danger { background: #fee2e2; color: #991b1b; }
.rf-chip-warn { background: #fef3c7; color: #92400e; }
.rf-chip-info { background: #f3f4f6; color: #6b7280; }
.rf-risk-hint { font-size: 11px; color: #b0b7c3; white-space: nowrap; }
.rf-risk-hint + .rf-risk-hint::before { content: '·'; margin: 0 2px; color: #d1d5db; }
.rf-risk-overflow { background: #fff; color: #6b7280; border: 1px dashed #d1d5db; cursor: pointer; }
.rf-no-risk { color: #d1d5db; }

/* ---- drawer ---- */
.rf-drawer-overlay { position: fixed; inset: 0; z-index: 2100; background: rgba(15,23,42,0.46); display: flex; justify-content: flex-end; }
.rf-drawer { width: 520px; max-width: 92vw; height: 100%; background: #fff; box-shadow: -8px 0 30px rgba(15,23,42,0.2); display: flex; flex-direction: column; overflow-y: auto; }
.rf-drawer-header { position: relative; padding: 20px 24px 12px; border-bottom: 1px solid #ebeef5; }
.rf-drawer-header h3 { margin: 0; font-size: 16px; color: #303133; }
.rf-drawer-header p { margin: 4px 0 0; font-size: 12px; color: #909399; }
.rf-drawer-close { position: absolute; top: 16px; right: 16px; background: none; border: none; font-size: 18px; cursor: pointer; color: #909399; }
.rf-drawer-body { padding: 16px 24px; flex: 1; overflow-y: auto; }
.rf-drawer-summary { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.rf-summary-item { font-size: 12px; color: #606266; display: inline-flex; align-items: center; gap: 6px; }

/* ---- metadata display ---- */
.rf-metadata-section { margin-top: 12px; padding-top: 12px; border-top: 1px solid #ebeef5; }
.rf-metadata-title { font-size: 12px; color: #909399; margin-bottom: 8px; font-weight: 500; }
.rf-metadata-json { margin: 0; padding: 10px 12px; background: #f8f9fb; border: 1px solid #e4e7ed; border-radius: 4px; font-size: 11px; font-family: 'Menlo', 'Monaco', monospace; line-height: 1.5; max-height: 160px; overflow-y: auto; white-space: pre-wrap; word-break: break-all; color: #303133; }

/* ---- governance dialog ---- */
.rf-gov-dialog { width: 440px; max-width: 92vw; margin: auto; background: #fff; border-radius: 8px; box-shadow: 0 12px 48px rgba(15,23,42,0.24); display: flex; flex-direction: column; max-height: 80vh; overflow-y: auto; align-self: center; }
.rf-gov-dialog-header { position: relative; padding: 18px 24px 14px; border-bottom: 1px solid #ebeef5; }
.rf-gov-dialog-header h3 { margin: 0; font-size: 15px; color: #303133; }
.rf-gov-dialog-body { padding: 16px 24px 20px; }
.rf-gov-scope-note { display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px; margin-bottom: 14px; background: #fefce8; border: 1px solid #fde68a; border-radius: 4px; font-size: 12px; color: #854d0e; line-height: 1.5; }
.rf-gov-dialog-body .rf-form-row label { min-width: 90px; }
.rf-gov-dialog-body .rf-form-row .form-input { flex: 1; min-width: 180px; }

@media (max-width: 1400px) {
  .query-form { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (max-width: 1100px) {
  .query-form { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .rf-batch-layout { grid-template-columns: 1fr; }
}

/* ---- flexible import ---- */
.rf-flexible-import { padding: 8px 0; }
.rf-flexible-mapping { margin-top: 8px; }
.rf-mapping-table { margin-top: 4px; }
.rf-mapping-table th { font-size: 12px; }
.rf-mapping-table td { vertical-align: middle; }
.rf-mapping-matched td { background: #f0fdf4; }
.rf-mapping-missing td { background: #fef2f2; }
.rf-mapping-select { width: 100%; min-width: 140px; }
.rf-flexible-unmapped { margin-top: 8px; font-size: 12px; color: #909399; line-height: 1.5; }
.rf-flexible-preview { margin-top: 12px; }
.rf-flexible-errors { margin-top: 10px; padding: 10px 12px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 4px; }
.rf-error-item { font-size: 12px; color: #991b1b; line-height: 1.6; }
.rf-error-item::before { content: '• '; }
</style>
